import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';

const DISMISS_KEY = 'accountSetupNudgeDismissedAt';
const REDISPLAY_DAYS = 7;

export function AccountSetupNudge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!user) return;

    // Check local dismiss first (cheap)
    const dismissedAt = localStorage.getItem(DISMISS_KEY);
    if (dismissedAt) {
      const daysSince = (Date.now() - Number(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSince < REDISPLAY_DAYS) return;
    }

    // Only show if user explicitly skipped onboarding (not just pre-dates the feature)
    supabase
      .from('profiles')
      .select('onboarding_completed_at, onboarding_skipped_at')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (!data) return;
        // Show nudge only if they skipped — not if both are null (pre-feature user)
        if (!data.onboarding_completed_at && data.onboarding_skipped_at) {
          setVisible(true);
        }
      });
  }, [user]);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="flex items-center gap-4 px-4 py-3 mb-6 bg-gold/10 border border-gold/20 rounded-xl">
      <div className="w-8 h-8 rounded-lg bg-gold/20 flex items-center justify-center shrink-0">
        <Sparkles className="h-4 w-4 text-gold" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-midnight dark:text-soft-white leading-snug">
          Personalise your SermonBuild experience
        </p>
        <p className="text-xs text-slate dark:text-soft-white/60 mt-0.5">
          Tell us about your ministry so AI content fits your tradition and context.
        </p>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => navigate('/account-setup')}
          className="text-xs font-semibold px-3 py-1.5 bg-gold text-white rounded-lg hover:bg-gold/90 transition-colors whitespace-nowrap"
        >
          Set up now
        </button>
        <button
          onClick={dismiss}
          className="p-1 text-slate/40 hover:text-slate dark:hover:text-soft-white/60 transition-colors"
          aria-label="Dismiss"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
